/**
 * Base-layer backends for `ClippingStudioCanvasService` (orchestrator lives in
 * `clipping-studio-canvas.service.ts`). Split into this module to keep the
 * service file focused on the RAF loop and callbacks.
 */
import type {
  CanvasRenderDecision,
  VideoFrameSource,
} from "./clipping-studio-canvas.service";
import type { ReframeConfig } from "app/js/src/domain/clipping-studio/clipping-studio-project";
import { drawReframeFrame } from "./clipping-studio-reframe-draw";

/** Contract for drawing the base (video) layer: WebGL2 or another implementation. */
export interface IBaseLayerBackend {
  isInit: boolean;
  canvas: HTMLCanvasElement | null;

  /** True when backend has everything required to start rendering. */
  readonly isReadyToRender: boolean;

  /** True when canvas has a valid (non-zero) pixel size. */
  readonly hasValidCanvasSize: boolean;

  /** Returns current canvas pixel size (0 if not available). */
  getCanvasSize(): { width: number; height: number };

  /** Reset backend to the initial (not initialized) state. */
  reset(): void;

  /** Resize the backend canvas (and viewport/state if needed). */
  resize(width: number, height: number): void;

  /** Draw the base video layer for this tick. */
  drawBaseLayer(frame: VideoFrameSource, decision: CanvasRenderDecision): void;

  /**
   * Backend-specific canvas initialization.
   * Implementations should fully initialize their internal state here.
   */
  initializeCanvas(): boolean;
}

/**
 * WebGL2 base layer: compiles the textured-quad program, owns GL handles, and
 * implements `drawBaseLayer` (clear, `texImage2D` from the video, optional SQZ
 * `viewport` on the current framebuffer).
 */
export class WebGlCanvasBackend implements IBaseLayerBackend {
  isInit = false;
  canvas: HTMLCanvasElement | null = null;
  gl: WebGL2RenderingContext | null = null;
  videoProgram: WebGLProgram | null = null;
  videoVao: WebGLVertexArrayObject | null = null;
  videoTexture: WebGLTexture | null = null;

  get isReadyToRender(): boolean {
    return this.isInit && this.canvas != null && this.gl != null;
  }

  get hasValidCanvasSize(): boolean {
    const c = this.canvas;
    return c != null && c.width > 0 && c.height > 0;
  }

  getCanvasSize(): { width: number; height: number } {
    const c = this.canvas;
    return { width: c?.width ?? 0, height: c?.height ?? 0 };
  }

  reset(): void {
    this.isInit = false;
    this.canvas = null;
    this.gl = null;
    this.videoProgram = null;
    this.videoVao = null;
    this.videoTexture = null;
  }

  resize(width: number, height: number): void {
    const canvas = this.canvas;
    const gl = this.gl;
    if (!canvas || !gl) return;

    canvas.width = width;
    canvas.height = height;
    gl.viewport(0, 0, width, height);
  }

  drawBaseLayer(frame: VideoFrameSource, decision: CanvasRenderDecision): void {
    const gl = this.gl;
    const canvas = this.canvas;
    const videoTexture = this.videoTexture;
    const videoProgram = this.videoProgram;
    const videoVao = this.videoVao;
    if (!gl || !canvas || !videoTexture || !videoProgram || !videoVao) return;

    gl.clear(gl.COLOR_BUFFER_BIT);
    gl.bindTexture(gl.TEXTURE_2D, videoTexture);
    gl.texImage2D(
      gl.TEXTURE_2D,
      0,
      gl.RGBA,
      gl.RGBA,
      gl.UNSIGNED_BYTE,
      frame.video,
    );

    const canvasW = canvas.width;
    const canvasH = canvas.height;

    // Fit the video into the full canvas first (non-SQZ appearance), then if SQZ is
    // active apply the SQZ viewport as a squeeze transform to that fitted rect.
    const videoW = frame.video.videoWidth;
    const videoH = frame.video.videoHeight;

    let fitX0 = 0;
    let fitY0 = 0;
    let fitW0 = canvasW;
    let fitH0 = canvasH;

    if (videoW > 0 && videoH > 0 && canvasW > 0 && canvasH > 0) {
      const scale = Math.min(canvasW / videoW, canvasH / videoH);
      fitW0 = Math.round(videoW * scale);
      fitH0 = Math.round(videoH * scale);
      fitX0 = Math.round((canvasW - fitW0) / 2);
      fitY0 = Math.round((canvasH - fitH0) / 2);
    }

    const sqz = decision.viewport;
    if (sqz && canvasW > 0 && canvasH > 0) {
      const sx = sqz.w / canvasW;
      const sy = sqz.h / canvasH;
      const x = Math.round(sqz.x + fitX0 * sx);
      const y = Math.round(sqz.y + fitY0 * sy);
      const w = Math.round(fitW0 * sx);
      const h = Math.round(fitH0 * sy);
      gl.viewport(x, y, w, h);
    } else {
      gl.viewport(fitX0, fitY0, fitW0, fitH0);
    }
    gl.useProgram(videoProgram);
    gl.bindVertexArray(videoVao);
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, videoTexture);
    const uTexture = gl.getUniformLocation(videoProgram, "u_texture");
    gl.uniform1i(uTexture, 0);
    gl.drawArrays(gl.TRIANGLES, 0, 6);
    gl.bindVertexArray(null);

    // Reset viewport for any subsequent draws this tick.
    gl.viewport(0, 0, canvas.width, canvas.height);
  }

  initializeCanvas(): boolean {
    const canvas = this.canvas;
    if (!canvas) {
      console.warn("WebGlCanvasBackend.initializeCanvas: canvas is null");
      return false;
    }

    const gl = canvas.getContext("webgl2", {
      antialias: false,
      depth: false,
      alpha: false,
      stencil: false,
    });
    if (!gl) {
      console.error("WebGlCanvasBackend: WebGL2 not available");
      return false;
    }

    gl.clearColor(0, 0, 0, 1);

    const vertexShader = WebGlCanvasBackend.createShader(
      gl,
      gl.VERTEX_SHADER,
      WebGlCanvasBackend.VERTEX_SHADER_SOURCE,
    );
    const fragmentShader = WebGlCanvasBackend.createShader(
      gl,
      gl.FRAGMENT_SHADER,
      WebGlCanvasBackend.FRAGMENT_SHADER_SOURCE,
    );
    if (!vertexShader || !fragmentShader) {
      console.warn(
        "WebGlCanvasBackend.initializeCanvas: shader compilation failed",
      );
      return false;
    }

    const videoProgram = WebGlCanvasBackend.createProgram(
      gl,
      vertexShader,
      fragmentShader,
    );
    if (!videoProgram) {
      console.warn(
        "WebGlCanvasBackend.initializeCanvas: program link failed",
      );
      return false;
    }

    const videoVao = WebGlCanvasBackend.setupQuadVao(gl, videoProgram);
    if (!videoVao) {
      console.warn("WebGlCanvasBackend.initializeCanvas: VAO setup failed");
      return false;
    }

    const videoTexture = WebGlCanvasBackend.createVideoTexture(gl);
    if (!videoTexture) {
      console.warn(
        "WebGlCanvasBackend.initializeCanvas: video texture creation failed",
      );
      return false;
    }

    this.gl = gl;
    this.videoProgram = videoProgram;
    this.videoVao = videoVao;
    this.videoTexture = videoTexture;

    return true;
  }

  private static readonly VERTEX_SHADER_SOURCE = `#version 300 es
in vec4 a_position;
in vec2 a_texcoord;
out vec2 v_texcoord;
void main() {
  gl_Position = a_position;
  v_texcoord = a_texcoord;
}`;

  private static readonly FRAGMENT_SHADER_SOURCE = `#version 300 es
precision mediump float;
in vec2 v_texcoord;
uniform sampler2D u_texture;
out vec4 outColor;
void main() {
  outColor = texture(u_texture, v_texcoord);
}`;

  /** Full-screen quad vertices (clip space): bottom-left, bottom-right, top-left, then top-left, bottom-right, top-right. */
  private static readonly QUAD_POSITIONS = new Float32Array([
    -1, -1, 1, -1, -1, 1, -1, 1, 1, -1, 1, 1,
  ]);

  /**
   * Texcoords for video right-side up and not mirrored.
   * Without UNPACK_FLIP_Y, texture (0,0)=top-left of video, (1,1)=bottom-right.
   * Screen: bottom-left (-1,-1)->(0,1), top-left (-1,1)->(0,0), etc.
   */
  private static readonly QUAD_TEXCOORDS = new Float32Array([
    0, 1, 1, 1, 0, 0, 0, 0, 1, 1, 1, 0,
  ]);

  private static createShader(
    gl: WebGL2RenderingContext,
    type: number,
    source: string,
  ): WebGLShader | null {
    const shader = gl.createShader(type);
    if (!shader) return null;
    gl.shaderSource(shader, source);
    gl.compileShader(shader);
    if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
      console.error(gl.getShaderInfoLog(shader));
      gl.deleteShader(shader);
      return null;
    }
    return shader;
  }

  private static createProgram(
    gl: WebGL2RenderingContext,
    vertexShader: WebGLShader,
    fragmentShader: WebGLShader,
  ): WebGLProgram | null {
    const program = gl.createProgram();
    if (!program) return null;
    gl.attachShader(program, vertexShader);
    gl.attachShader(program, fragmentShader);
    gl.linkProgram(program);
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
      console.error(gl.getProgramInfoLog(program));
      gl.deleteProgram(program);
      return null;
    }
    return program;
  }

  private static createBuffer(
    gl: WebGL2RenderingContext,
    data: Float32Array,
  ): WebGLBuffer | null {
    const buffer = gl.createBuffer();
    if (!buffer) return null;
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    gl.bufferData(gl.ARRAY_BUFFER, data, gl.STATIC_DRAW);
    return buffer;
  }

  private static setupQuadVao(
    gl: WebGL2RenderingContext,
    program: WebGLProgram,
  ): WebGLVertexArrayObject | null {
    const vao = gl.createVertexArray();
    if (!vao) return null;
    gl.bindVertexArray(vao);

    WebGlCanvasBackend.createBuffer(gl, WebGlCanvasBackend.QUAD_POSITIONS);
    const positionLoc = gl.getAttribLocation(program, "a_position");
    gl.enableVertexAttribArray(positionLoc);
    gl.vertexAttribPointer(positionLoc, 2, gl.FLOAT, false, 0, 0);

    WebGlCanvasBackend.createBuffer(gl, WebGlCanvasBackend.QUAD_TEXCOORDS);
    const texcoordLoc = gl.getAttribLocation(program, "a_texcoord");
    gl.enableVertexAttribArray(texcoordLoc);
    gl.vertexAttribPointer(texcoordLoc, 2, gl.FLOAT, false, 0, 0);

    gl.bindVertexArray(null);
    return vao;
  }

  private static createVideoTexture(gl: WebGL2RenderingContext): WebGLTexture | null {
    const texture = gl.createTexture();
    if (!texture) return null;
    gl.bindTexture(gl.TEXTURE_2D, texture);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    return texture;
  }
}

/**
 * 2D base layer: acquires a `CanvasRenderingContext2D` in `initializeCanvas`.
 * `drawBaseLayer` remains a no-op until reframe drawing is wired here.
 */
export class Canvas2dCanvasBackend implements IBaseLayerBackend {
  isInit = false;
  canvas: HTMLCanvasElement | null = null;
  ctx: CanvasRenderingContext2D | null = null;
  reframeConfig: ReframeConfig | null = null;

  setReframeConfig(cfg: ReframeConfig | null): void {
    this.reframeConfig = cfg;
  }

  get isReadyToRender(): boolean {
    return this.isInit && this.canvas != null && this.ctx != null;
  }

  get hasValidCanvasSize(): boolean {
    const c = this.canvas;
    return c != null && c.width > 0 && c.height > 0;
  }

  getCanvasSize(): { width: number; height: number } {
    const c = this.canvas;
    return { width: c?.width ?? 0, height: c?.height ?? 0 };
  }

  reset(): void {
    this.isInit = false;
    this.canvas = null;
    this.ctx = null;
    this.reframeConfig = null;
  }

  resize(width: number, height: number): void {
    const canvas = this.canvas;

    if (!canvas) return;

    canvas.width = width;
    canvas.height = height;
  }

  drawBaseLayer(frame: VideoFrameSource, decision: CanvasRenderDecision): void {
    const canvas = this.canvas;
    const ctx = this.ctx;
    const cfg = this.reframeConfig;
    if (!canvas || !ctx || !cfg) return;

    const dst = decision.dstRect2d;
    if (dst) {
      // SQZ in reframe mode: squeeze the whole reframed frame into dstRect2d.
      // Outside dstRect2d stays black (same visual rule as WebGL clearColor).
      ctx.fillStyle = "#000";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
    }
    drawReframeFrame(frame.video, ctx, canvas.width, canvas.height, cfg, dst);
  }

  initializeCanvas(): boolean {
    const canvas = this.canvas;
    if (!canvas) {
      console.warn("Canvas2dCanvasBackend.initializeCanvas: canvas is null");
      return false;
    }

    const ctx = canvas.getContext("2d");
    if (!ctx) {
      console.warn("Canvas2dCanvasBackend.initializeCanvas: 2D context unavailable");
      return false;
    }

    this.ctx = ctx;
    return true;
  }
}

